import {
  IonBreadcrumb,
  IonBreadcrumbs,
  IonButton,
  IonContent,
  IonIcon,
  IonRow,
  IonText
} from '@ionic/react';
import ListItem from 'components/items/list-item/ListItem';
import SearchBar from 'components/searchbar/SearchBar';
import {
  INVENTORY_COLORS_BREADCRUMB_LABEL,
  INVENTORY_OPTIONS_BREADCRUMB_LABEL
} from 'core/constants';
import LoadingIonList from 'core/layout/loading-ion-list/LoadingIonList';
import React from 'react';
import ArrowRight from 'assets/icons/arrow-right.svg';
import HomeSelectionIcon from 'assets/icons/dashboard.svg';
import PaintIcon from 'assets/icons/paint.svg';
import { ProductGroup } from 'features/shared/inventory/store/InventorySlice';
import { iconForGroup } from 'features/shared/inventory/utils/inventoryGroupIcons';
import 'features/shared/inventory/InventoryGroups.scss';

interface InventoryGroupListViewDesktopProps {
  loading: boolean;
  productGroups: ProductGroup[];
  search: string;
  onSearchChange: (val: string) => void;
  onAddItem: () => void;
  optionsCount: number;
  colorsCount: number;
  standardProductGroups: ProductGroup[];
  totalItemCount: number;
  onShowGroup: (id: number) => void;
  onNavigateToHomeSelections: () => void;
  onNavigateToColorSelections: () => void;
  searchResultsView: React.ReactNode;
}

const InventoryGroupListViewDesktop: React.FC<
  InventoryGroupListViewDesktopProps
> = ({
  loading,
  productGroups,
  search,
  onSearchChange,
  onAddItem,
  optionsCount,
  colorsCount,
  standardProductGroups,
  totalItemCount,
  onShowGroup,
  onNavigateToHomeSelections,
  onNavigateToColorSelections,
  searchResultsView
}) => {
  return (
    <>
      <IonRow className="hk-desktop-digital-inventory-group-header">
        <div className="hk-desktop-digital-inventory-group-header-container">
          <div className="hk-desktop-digital-inventory-group-header-left">
            <IonBreadcrumbs className="hk-desktop-digital-inventory-group-header-breadcrumbs">
              <IonBreadcrumb>Inventory</IonBreadcrumb>
            </IonBreadcrumbs>
            {!loading && (
              <IonText
                color="medium"
                className="hk-desktop-digital-inventory-group-total-count"
              >
                {totalItemCount} Item{totalItemCount === 1 ? '' : 's'}
              </IonText>
            )}
          </div>
          <div className="hk-desktop-digital-inventory-group-header-right">
            <div className="hk-desktop-digital-inventory-group-search">
              <SearchBar
                value={search}
                disabled={productGroups?.length === 0}
                debounce={1000}
                onSearchChange={onSearchChange}
              />
            </div>
            <div className="hk-desktop-digital-inventory-group-header-button">
              <IonButton
                expand="block"
                color="primary"
                strong={true}
                onClick={onAddItem}
              >
                Add Item
              </IonButton>
            </div>
          </div>
        </div>
      </IonRow>
      <IonContent className="hk-desktop-digital-inventory-group">
        <LoadingIonList
          className="hk-desktop-digital-inventory-group-list"
          label={'Items In Inventory'}
          description={'There are no items of this kind added yet.'}
          items={productGroups}
          loading={loading}
        >
          {!!search ? (
            searchResultsView
          ) : (
            <>
              {optionsCount > 0 ? (
                <ListItem
                  key="home-option-selections"
                  customView={true}
                  className="hk-inventory-home-selections-card"
                  onClick={onNavigateToHomeSelections}
                >
                  <IonIcon
                    slot="start"
                    icon={HomeSelectionIcon}
                    className="hk-desktop-digital-inventory-group-list-item-start-icon"
                    color="dark"
                  />
                  <div className="hk-desktop-digital-inventory-group-list-item-body">
                    <IonText className="hk-desktop-digital-inventory-group-list-item-body-title">
                      <h2>{INVENTORY_OPTIONS_BREADCRUMB_LABEL}</h2>
                    </IonText>
                    <IonText className="hk-desktop-digital-inventory-group-list-item-body-description">
                      <p>{`${optionsCount} item(s)`}</p>
                    </IonText>
                    <IonIcon
                      slot="end"
                      icon={ArrowRight}
                      className="hk-desktop-digital-inventory-group-list-item-body-end-icon"
                    />
                  </div>
                </ListItem>
              ) : null}
              {colorsCount > 0 ? (
                <ListItem
                  key="home-color-selections"
                  customView={true}
                  className="hk-inventory-home-selections-card hk-inventory-color-selections-card"
                  onClick={onNavigateToColorSelections}
                >
                  <IonIcon
                    slot="start"
                    icon={PaintIcon}
                    className="hk-desktop-digital-inventory-group-list-item-start-icon"
                    color="dark"
                  />
                  <div className="hk-desktop-digital-inventory-group-list-item-body">
                    <IonText className="hk-desktop-digital-inventory-group-list-item-body-title">
                      <h2>{INVENTORY_COLORS_BREADCRUMB_LABEL}</h2>
                    </IonText>
                    <IonText className="hk-desktop-digital-inventory-group-list-item-body-description">
                      <p>{`${colorsCount} item(s)`}</p>
                    </IonText>
                    <IonIcon
                      slot="end"
                      icon={ArrowRight}
                      className="hk-desktop-digital-inventory-group-list-item-body-end-icon"
                    />
                  </div>
                </ListItem>
              ) : null}
              {standardProductGroups.map((g, i) => {
                const { id, group, count } = g;
                return (
                  <ListItem
                    key={`item-${i}`}
                    customView={true}
                    onClick={() => onShowGroup(id)}
                  >
                    <IonIcon
                      slot="start"
                      icon={iconForGroup(id)}
                      className="hk-desktop-digital-inventory-group-list-item-start-icon"
                      color="dark"
                    />
                    <div className="hk-desktop-digital-inventory-group-list-item-body">
                      <IonText className="hk-desktop-digital-inventory-group-list-item-body-title">
                        <h2>{group}</h2>
                      </IonText>
                      <IonText className="hk-desktop-digital-inventory-group-list-item-body-description">
                        <p>{count + ' item(s)'}</p>
                      </IonText>
                      <IonIcon
                        slot="end"
                        icon={ArrowRight}
                        className="hk-desktop-digital-inventory-group-list-item-body-end-icon"
                      />
                    </div>
                  </ListItem>
                );
              })}
            </>
          )}
        </LoadingIonList>
      </IonContent>
    </>
  );
};

export default InventoryGroupListViewDesktop;
